import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useCVContext } from '@/context/CVContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import Header from '@/components/Header';
import { 
  ArrowLeft, Download, Copy, CheckCircle, Briefcase, 
  Linkedin, Brain, Award, MapPin, Sparkles, Target,
  PenLine, ArrowRight, Eye, FileText, Star, BarChart3,
  Lightbulb, Users, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadPersonalityPdf } from '@/services/pdfService';
import { useDownloadGate } from '@/hooks/useDownloadGate';
import DownloadGateModal from '@/components/DownloadGateModal';

export default function PersonalityResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();
  const { state: cvState } = useCVContext();
  const reportRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false); // For print-optimized layout
  const [resultData, setResultData] = useState(null);
  const [code, setCode] = useState('');
  const [hasCVProfile, setHasCVProfile] = useState(false);
  const [cvCompletionPercent, setCvCompletionPercent] = useState(0);
  
  const profile = cvState?.profile;
  
  // Download gate hook
  const {
    showModal: showDownloadGate,
    initiateDownload,
    onLeadCaptured,
    closeModal: closeDownloadGate,
    downloadType
  } = useDownloadGate(profile?.id);
  
  // Function to calculate completion percentage locally
  const calculateLocalCompletion = (profile) => {
    if (!profile) return 0;
    
    let score = 0;
    let total = 100;
    
    // Identity (25%)
    if (profile.first_name) score += 10;
    if (profile.last_name) score += 10;
    if (profile.bio) score += 5;
    
    // Contact (10%)
    if (profile.contact?.email || profile.email) score += 5;
    if (profile.contact?.phone || profile.phone) score += 5;
    
    // Target Job (5%)
    if (profile.target_job) score += 5;
    
    // Experience (25%)
    if (profile.experience && profile.experience.length > 0) {
      score += Math.min(25, profile.experience.length * 8);
    }
    
    // Education (15%)
    if (profile.education && profile.education.length > 0) {
      score += Math.min(15, profile.education.length * 8);
    }
    
    // Skills (10%)
    if (profile.skills && profile.skills.length > 0) {
      score += Math.min(10, profile.skills.length * 2);
    }
    
    // Languages (5%)
    if (profile.languages && profile.languages.length > 0) {
      score += 5;
    }
    
    // Certifications (5%)
    if (profile.certifications && profile.certifications.length > 0) {
      score += 5;
    }
    
    return Math.min(100, Math.round(score));
  };
  
  // Check for CV profile in both context and localStorage
  useEffect(() => {
    // Check context first
    if (cvState?.profile?.id) {
      setHasCVProfile(true);
      const percent = cvState.profile?.completion_percentage || calculateLocalCompletion(cvState.profile);
      setCvCompletionPercent(percent);
    } else {
      // Check localStorage
      const savedProfile = localStorage.getItem('cvProfile');
      if (savedProfile) {
        try {
          const profile = JSON.parse(savedProfile);
          if (profile && profile.id) {
            setHasCVProfile(true);
            const percent = profile.completion_percentage || calculateLocalCompletion(profile);
            setCvCompletionPercent(percent);
          }
        } catch (e) {
          console.error('Error parsing CV profile:', e);
        }
      }
    }
  }, [cvState]);

  useEffect(() => {
    // Try to get result from location.state first, then localStorage
    if (location.state?.result) {
      setResultData(location.state.result);
      setCode(location.state.retrievalCode || '');
    } else {
      // Check localStorage
      const saved = localStorage.getItem('personalityResult');
      if (saved) {
        const parsed = JSON.parse(saved);
        setResultData(parsed.result);
        setCode(parsed.retrievalCode || '');
      } else {
        navigate('/personality-test');
      }
    }
  }, [location.state, navigate]);

  if (!resultData) return null;

  const { big_five_scores, persona } = resultData;
  const retrievalCode = code;
  const isEnglish = language === 'en';

  // Safe access to persona properties with fallbacks
  const personaName = isEnglish 
    ? (persona?.name_en || persona?.nameEn || persona?.name || 'Professional')
    : (persona?.name_fr || persona?.nameFr || persona?.name || 'Professionnel');
  const personaTagline = isEnglish 
    ? (persona?.tagline_en || persona?.taglineEn || persona?.tagline || '')
    : (persona?.tagline_fr || persona?.taglineFr || persona?.tagline || '');
  const personaDescription = isEnglish 
    ? (persona?.description_en || persona?.descriptionEn || persona?.description || '')
    : (persona?.description_fr || persona?.descriptionFr || persona?.description || '');
  const personaStrengths = isEnglish 
    ? (persona?.strengths_en || persona?.strengthsEn || persona?.strengths || [])
    : (persona?.strengths_fr || persona?.strengthsFr || persona?.strengths || []);
  const marketEdge = isEnglish 
    ? (persona?.market_edge_en || persona?.marketEdgeEn || persona?.market_edge || '')
    : (persona?.market_edge_fr || persona?.marketEdgeFr || persona?.market_edge || '');
  const cvBlurb = isEnglish 
    ? (persona?.cv_blurb_en || persona?.cvBlurbEn || persona?.cv_blurb || '')
    : (persona?.cv_blurb_fr || persona?.cvBlurbFr || persona?.cv_blurb || '');
  const interviewTips = isEnglish 
    ? (persona?.interview_tips_en || persona?.interviewTipsEn || persona?.interview_tips || [])
    : (persona?.interview_tips_fr || persona?.interviewTipsFr || persona?.interview_tips || []);

  const copyCode = () => {
    navigator.clipboard.writeText(retrievalCode);
    setCopied(true);
    toast.success(isEnglish ? 'Code copied!' : 'Code copié !');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPDF = async () => {
    await initiateDownload(executeDownloadPDF, 'personality_report');
  };

  const executeDownloadPDF = async () => {
    if (!profile?.id) {
      toast.error(isEnglish ? 'Error: Profile not found' : 'Erreur: Profil non trouvé');
      return;
    }
    
    setDownloading(true);
    toast.loading(isEnglish ? 'Generating PDF...' : 'Génération du PDF...', { id: 'pdf-loading' });
    
    try {
      // Use server-side PDF generation for professional quality
      await downloadPersonalityPdf(
        profile.id,
        isEnglish ? 'en' : 'fr',
        profile?.first_name || 'Personality',
        profile?.last_name || ''
      );
      
      toast.dismiss('pdf-loading');
      toast.success(isEnglish ? 'PDF downloaded!' : 'PDF téléchargé !');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.dismiss('pdf-loading');
      toast.error(isEnglish ? 'Error generating PDF' : 'Erreur lors de la génération');
    } finally {
      setDownloading(false);
    }
  };

  const shareOnLinkedIn = () => {
    const text = isEnglish 
      ? `Check out my professional personality profile! I'm "${personaName}" - ${personaTagline}. #ProfessionalDevelopment #CareerGrowth`
      : `Découvrez mon profil de personnalité professionnelle ! Je suis "${personaName}" - ${personaTagline}. #DéveloppementProfessionnel #Carrière`;
    const url = 'https://edomatch.com';
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`, '_blank');
  };

  const getPersonaColor = () => {
    const colors = {
      blue: '#3B82F6',
      purple: '#8B5CF6',
      green: '#10B981',
      orange: '#F59E0B'
    };
    return colors[persona.color] || colors.purple;
  };

  // Professional trait labels for recruiter view
  const traitLabels = isEnglish ? {
    openness: { name: 'Innovation & Creativity', low: 'Process-Oriented', high: 'Creative Thinker' },
    conscientiousness: { name: 'Reliability & Organization', low: 'Flexible', high: 'Highly Organized' },
    extraversion: { name: 'Communication & Teamwork', low: 'Independent Worker', high: 'Collaborative Leader' },
    agreeableness: { name: 'Collaboration & Adaptability', low: 'Results-Focused', high: 'Team Player' },
    neuroticism: { name: 'Stress Management', low: 'Highly Resilient', high: 'Detail-Conscious' }
  } : {
    openness: { name: 'Innovation & Créativité', low: 'Orienté processus', high: 'Penseur créatif' },
    conscientiousness: { name: 'Fiabilité & Organisation', low: 'Flexible', high: 'Très organisé' },
    extraversion: { name: 'Communication & Travail d\'équipe', low: 'Travailleur autonome', high: 'Leader collaboratif' },
    agreeableness: { name: 'Collaboration & Adaptabilité', low: 'Orienté résultats', high: 'Esprit d\'équipe' },
    neuroticism: { name: 'Gestion du stress', low: 'Très résilient', high: 'Attentif aux détails' }
  };

  const getTraitDescription = (trait, score) => {
    const adjustedScore = trait === 'neuroticism' ? 100 - score : score;
    const labels = traitLabels[trait];
    if (adjustedScore >= 70) return labels.high;
    if (adjustedScore <= 30) return labels.low;
    return isEnglish ? 'Balanced' : 'Équilibré';
  };

  // Detailed interpretation for each Big Five trait
  const getTraitInterpretation = (trait, adjustedScore, isEnglish) => {
    const interpretations = {
      openness: {
        high: {
          fr: "Vous êtes naturellement attiré par l'innovation et les nouvelles idées. Vous excellez dans les environnements qui encouragent la créativité et l'expérimentation. Votre curiosité vous pousse à explorer de nouvelles approches et à remettre en question le statu quo.",
          en: "You are naturally drawn to innovation and new ideas. You excel in environments that encourage creativity and experimentation. Your curiosity drives you to explore new approaches and challenge the status quo."
        },
        low: {
          fr: "Vous appréciez la stabilité et les méthodes éprouvées. Votre pragmatisme vous permet d'optimiser les processus existants et d'assurer la cohérence. Vous êtes fiable dans l'exécution des procédures établies.",
          en: "You appreciate stability and proven methods. Your pragmatism allows you to optimize existing processes and ensure consistency. You are reliable in executing established procedures."
        },
        balanced: {
          fr: "Vous savez équilibrer innovation et tradition. Vous pouvez proposer de nouvelles idées tout en respectant les processus existants quand ils fonctionnent bien.",
          en: "You know how to balance innovation and tradition. You can propose new ideas while respecting existing processes when they work well."
        }
      },
      conscientiousness: {
        high: {
          fr: "Votre rigueur et votre organisation sont exceptionnelles. Vous excellez dans la planification, le respect des délais et la qualité du travail. Les managers peuvent compter sur vous pour livrer des résultats constants.",
          en: "Your rigor and organization are exceptional. You excel at planning, meeting deadlines, and quality of work. Managers can count on you to deliver consistent results."
        },
        low: {
          fr: "Votre flexibilité vous permet de vous adapter rapidement aux changements. Vous êtes à l'aise avec l'ambiguïté et pouvez pivoter facilement quand les priorités changent.",
          en: "Your flexibility allows you to adapt quickly to changes. You are comfortable with ambiguity and can pivot easily when priorities change."
        },
        balanced: {
          fr: "Vous combinez organisation et flexibilité. Vous savez planifier tout en restant adaptable face aux imprévus.",
          en: "You combine organization and flexibility. You know how to plan while remaining adaptable to unexpected situations."
        }
      },
      extraversion: {
        high: {
          fr: "Vous êtes énergisé par les interactions sociales et le travail d'équipe. Votre capacité à communiquer et à fédérer fait de vous un leader naturel dans les projets collaboratifs.",
          en: "You are energized by social interactions and teamwork. Your ability to communicate and unite others makes you a natural leader in collaborative projects."
        },
        low: {
          fr: "Vous êtes productif en travail autonome et excellez dans les tâches nécessitant concentration et réflexion approfondie. Votre écoute attentive fait de vous un observateur précieux.",
          en: "You are productive in independent work and excel at tasks requiring concentration and deep thinking. Your attentive listening makes you a valuable observer."
        },
        balanced: {
          fr: "Vous naviguez aisément entre travail d'équipe et travail autonome. Vous savez quand prendre la parole et quand écouter.",
          en: "You navigate easily between teamwork and independent work. You know when to speak up and when to listen."
        }
      },
      agreeableness: {
        high: {
          fr: "Votre empathie et votre sens de la collaboration créent une dynamique d'équipe positive. Vous excellez dans la médiation et la construction de consensus.",
          en: "Your empathy and collaborative spirit create a positive team dynamic. You excel at mediation and building consensus."
        },
        low: {
          fr: "Votre objectivité vous permet de prendre des décisions difficiles. Vous savez défendre vos positions et négocier fermement pour obtenir les meilleurs résultats.",
          en: "Your objectivity allows you to make difficult decisions. You know how to defend your positions and negotiate firmly to achieve the best results."
        },
        balanced: {
          fr: "Vous savez être diplomate tout en défendant vos convictions. Vous adaptez votre approche selon les situations.",
          en: "You know how to be diplomatic while defending your convictions. You adapt your approach according to situations."
        }
      },
      neuroticism: {
        high: {
          fr: "Votre calme et votre stabilité émotionnelle sont des atouts majeurs. Vous restez performant sous pression et servez de point d'ancrage pour votre équipe dans les moments difficiles.",
          en: "Your calm and emotional stability are major assets. You remain effective under pressure and serve as an anchor for your team during difficult times."
        },
        low: {
          fr: "Votre sensibilité vous rend attentif aux détails et aux subtilités. Vous anticipez les problèmes potentiels et apportez une vigilance précieuse à vos projets.",
          en: "Your sensitivity makes you attentive to details and subtleties. You anticipate potential problems and bring valuable vigilance to your projects."
        },
        balanced: {
          fr: "Vous gérez bien le stress tout en restant sensible aux enjeux. Vous savez quand il faut être vigilant et quand il faut lâcher prise.",
          en: "You manage stress well while remaining sensitive to issues. You know when to be vigilant and when to let go."
        }
      }
    };

    const level = adjustedScore >= 70 ? 'high' : adjustedScore <= 30 ? 'low' : 'balanced';
    return interpretations[trait]?.[level]?.[isEnglish ? 'en' : 'fr'] || '';
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'rgb(241, 245, 249)' }}>
      <Header />
      
      <main className="max-w-4xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Action Bar - Same as CV Preview */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-4 sm:mb-6 bg-white/70 backdrop-blur-xl rounded-2xl p-3 border border-gray-200 shadow-sm">
          <Button variant="outline" onClick={() => navigate('/')} className="gap-2 w-full sm:w-auto">
            <ArrowLeft className="w-4 h-4" />
            {isEnglish ? 'Back' : 'Retour'}
          </Button>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <Button onClick={() => navigate('/complete-profile')} variant="outline" className="gap-2 border-purple-300 text-purple-600 hover:bg-purple-50 w-full sm:w-auto">
              <Sparkles className="w-4 h-4" />
              {isEnglish ? 'Complete Profile' : 'Profil Complet'}
            </Button>
            
            <Button onClick={handleDownloadPDF} disabled={downloading} className="gap-2 bg-[#5BA946] hover:bg-[#4a9339] w-full sm:w-auto">
              <Download className="w-4 h-4" />
              {isEnglish ? 'Download PDF' : 'Télécharger PDF'}
            </Button>
            
            <Button onClick={shareOnLinkedIn} variant="outline" className="gap-2 border-[#0077B5] text-[#0077B5] hover:bg-[#0077B5]/10 w-full sm:w-auto">
              <Linkedin className="w-4 h-4" />
              LinkedIn
            </Button>
          </div>
        </div>

        {/* Professional Personality Profile Document */}
        <Card className="shadow-xl overflow-hidden">
          <div ref={reportRef} className={`bg-white ${isPrinting ? 'print-mode' : ''}`} style={{ backgroundColor: '#ffffff' }}>
            
            {/* Document Header - Print Optimized */}
            <div style={{ padding: isPrinting ? '24px' : '32px', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '24px' }}>
                {/* Persona Icon */}
                <div 
                  style={{ 
                    width: isPrinting ? '80px' : '96px', 
                    height: isPrinting ? '80px' : '96px',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isPrinting ? '36px' : '48px',
                    flexShrink: 0,
                    backgroundColor: `${getPersonaColor()}15`, 
                    border: `3px solid ${getPersonaColor()}`,
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {persona.icon}
                </div>
                
                <div style={{ flex: 1 }}>
                  <p style={{ 
                    fontSize: isPrinting ? '11px' : '14px', 
                    fontWeight: 600, 
                    color: '#6b7280', 
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    {isEnglish ? 'Professional Personality Profile' : 'Profil de Personnalité Professionnelle'}
                  </p>
                  <h1 style={{ 
                    fontSize: isPrinting ? '24px' : '28px', 
                    fontWeight: 'bold', 
                    color: '#111827', 
                    marginBottom: '8px',
                    lineHeight: 1.2
                  }}>{personaName}</h1>
                  <p style={{ 
                    fontSize: isPrinting ? '14px' : '18px', 
                    fontWeight: 600, 
                    color: getPersonaColor() 
                  }}>{personaTagline}</p>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div style={{ padding: isPrinting ? '24px' : '32px' }}>
              
              {/* Executive Summary */}
              <section style={{ marginBottom: isPrinting ? '20px' : '32px' }}>
                <h2 style={{ 
                  fontSize: isPrinting ? '15px' : '18px', 
                  fontWeight: 'bold', 
                  color: '#111827', 
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{ 
                    width: isPrinting ? '24px' : '32px', 
                    height: isPrinting ? '24px' : '32px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: `${getPersonaColor()}15` 
                  }}>
                    <Briefcase style={{ width: isPrinting ? '12px' : '16px', height: isPrinting ? '12px' : '16px', color: getPersonaColor() }} />
                  </div>
                  {isEnglish ? 'Executive Summary' : 'Résumé Exécutif'}
                </h2>
                <div style={{ 
                  backgroundColor: '#f9fafb', 
                  borderRadius: '12px', 
                  padding: isPrinting ? '16px' : '20px' 
                }}>
                  <p style={{ 
                    color: '#374151', 
                    lineHeight: 1.6,
                    fontSize: isPrinting ? '12px' : '14px'
                  }}>{personaDescription}</p>
                </div>
              </section>

              {/* Professional Bio - Ready to use */}
              <section style={{ marginBottom: isPrinting ? '20px' : '32px' }}>
                <h2 style={{ 
                  fontSize: isPrinting ? '15px' : '18px', 
                  fontWeight: 'bold', 
                  color: '#111827', 
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{ 
                    width: isPrinting ? '24px' : '32px', 
                    height: isPrinting ? '24px' : '32px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: `${getPersonaColor()}15` 
                  }}>
                    <FileText style={{ width: isPrinting ? '12px' : '16px', height: isPrinting ? '12px' : '16px', color: getPersonaColor() }} />
                  </div>
                  {isEnglish ? 'Professional Bio' : 'Bio Professionnelle'}
                </h2>
                <div style={{ 
                  borderLeft: `4px solid ${getPersonaColor()}`,
                  paddingLeft: '16px',
                  paddingTop: '8px',
                  paddingBottom: '8px',
                  marginLeft: isPrinting ? '8px' : '0'
                }}>
                  <p style={{ 
                    color: '#1f2937', 
                    fontStyle: 'italic', 
                    lineHeight: 1.6,
                    fontSize: isPrinting ? '13px' : '18px'
                  }}>&ldquo;{cvBlurb}&rdquo;</p>
                </div>
              </section>

              {/* Personality Competencies - Balanced Scale Visual */}
              <section>
                <h2 className={`font-bold text-gray-900 mb-4 flex items-center gap-2 ${isPrinting ? 'text-base' : 'text-lg'}`}>
                  <div className={`rounded-lg flex items-center justify-center ${isPrinting ? 'w-6 h-6' : 'w-8 h-8'}`} style={{ backgroundColor: `${getPersonaColor()}15` }}>
                    <Brain className={`${isPrinting ? 'w-3 h-3' : 'w-4 h-4'}`} style={{ color: getPersonaColor() }} />
                  </div>
                  {isEnglish ? 'Professional Competencies' : 'Compétences Professionnelles'}
                </h2>
                <div className={`${isPrinting ? 'space-y-3' : 'space-y-4'}`}>
                  {Object.entries(big_five_scores).map(([trait, score]) => {
                    const adjustedScore = trait === 'neuroticism' ? 100 - score : score;
                    const labels = traitLabels[trait];
                    // Calculate position from center: -50 (full left) to +50 (full right)
                    const centerOffset = adjustedScore - 50;
                    const dominantSide = centerOffset >= 0 ? 'high' : 'low';
                    const dominantLabel = dominantSide === 'high' ? labels.high : labels.low;
                    
                    return (
                      <div key={trait} className={`bg-gray-50 rounded-xl ${isPrinting ? 'p-3' : 'p-4'}`}>
                        <div className="flex justify-between items-center mb-3">
                          <span className={`font-semibold text-gray-800 ${isPrinting ? 'text-sm' : ''}`}>{labels.name}</span>
                          <Badge 
                            className={`font-medium ${isPrinting ? 'text-[10px] px-2 py-0.5' : 'text-xs'}`}
                            style={{ backgroundColor: `${getPersonaColor()}15`, color: getPersonaColor() }}
                          >
                            {dominantLabel}
                          </Badge>
                        </div>
                        
                        {/* Balanced scale - Center represents equilibrium */}
                        <div className="relative">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`font-medium transition-all ${isPrinting ? 'text-[10px]' : 'text-xs'} ${dominantSide === 'low' ? 'text-gray-800' : 'text-gray-400'}`}>
                              {labels.low}
                            </span>
                            <span className={`text-gray-300 ${isPrinting ? 'text-[10px]' : 'text-xs'}`}>|</span>
                            <span className={`font-medium transition-all ${isPrinting ? 'text-[10px]' : 'text-xs'} ${dominantSide === 'high' ? 'text-gray-800' : 'text-gray-400'}`}>
                              {labels.high}
                            </span>
                          </div>
                          
                          {/* Balanced bar with center marker */}
                          <div className={`relative bg-gray-200 rounded-full overflow-hidden ${isPrinting ? 'h-2' : 'h-3'}`}>
                            {/* Center line indicator */}
                            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-300 z-10" />
                            
                            {/* Fill from center toward dominant side */}
                            {centerOffset >= 0 ? (
                              <div 
                                className="absolute top-0 bottom-0 left-1/2 rounded-r-full"
                                style={{ 
                                  width: `${Math.abs(centerOffset)}%`, 
                                  backgroundColor: getPersonaColor() 
                                }}
                              />
                            ) : (
                              <div 
                                className="absolute top-0 bottom-0 rounded-l-full"
                                style={{ 
                                  width: `${Math.abs(centerOffset)}%`, 
                                  right: '50%',
                                  backgroundColor: getPersonaColor() 
                                }}
                              />
                            )}
                            
                            {/* Position marker */}
                            <div 
                              className={`absolute top-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-md z-20 ${isPrinting ? 'w-3 h-3' : 'w-4 h-4'}`}
                              style={{ 
                                left: `${adjustedScore}%`, 
                                transform: 'translate(-50%, -50%)',
                                backgroundColor: getPersonaColor()
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Key Strengths */}
              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${getPersonaColor()}15` }}>
                    <Award className="w-4 h-4" style={{ color: getPersonaColor() }} />
                  </div>
                  {isEnglish ? 'Key Strengths' : 'Points Forts'}
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {personaStrengths.map((strength, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-xl p-4">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${getPersonaColor()}15` }}>
                        <Star className="w-4 h-4" style={{ color: getPersonaColor() }} />
                      </div>
                      <span className="text-gray-800 font-medium">{strength}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* Regional Context */}
              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${getPersonaColor()}15` }}>
                    <MapPin className="w-4 h-4" style={{ color: getPersonaColor() }} />
                  </div>
                  {isEnglish ? 'Regional Market Fit' : 'Adaptation au Marché Régional'}
                </h2>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <p className="text-gray-700 leading-relaxed">{marketEdge}</p>
                </div>
              </section>

              {/* Interview Tips */}
              {interviewTips && interviewTips.length > 0 && (
                <section>
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${getPersonaColor()}15` }}>
                      <Target className="w-4 h-4" style={{ color: getPersonaColor() }} />
                    </div>
                    {isEnglish ? 'Interview Success Tips' : 'Conseils pour Réussir vos Entretiens'}
                  </h2>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                    <ul className="space-y-3">
                      {interviewTips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-blue-600 text-sm font-bold">{idx + 1}</span>
                          </div>
                          <p className="text-gray-700 leading-relaxed">{tip}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>
              )}

              {/* Detailed Big Five Analysis */}
              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${getPersonaColor()}15` }}>
                    <BarChart3 className="w-4 h-4" style={{ color: getPersonaColor() }} />
                  </div>
                  {isEnglish ? 'Detailed Personality Analysis' : 'Analyse Détaillée de la Personnalité'}
                </h2>
                <div className="grid gap-4">
                  {Object.entries(big_five_scores).map(([trait, score]) => {
                    const adjustedScore = trait === 'neuroticism' ? 100 - score : score;
                    const labels = traitLabels[trait];
                    const interpretation = getTraitInterpretation(trait, adjustedScore, isEnglish);
                    
                    return (
                      <div key={trait} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-800">{labels.name}</h3>
                          <Badge 
                            className="text-xs font-medium"
                            style={{ backgroundColor: `${getPersonaColor()}15`, color: getPersonaColor() }}
                          >
                            {getTraitDescription(trait, score)}
                          </Badge>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed">{interpretation}</p>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* How to Use This Report */}
              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${getPersonaColor()}15` }}>
                    <Lightbulb className="w-4 h-4" style={{ color: getPersonaColor() }} />
                  </div>
                  {isEnglish ? 'How to Use This Report' : 'Comment Utiliser Ce Rapport'}
                </h2>
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 text-sm mb-1">
                          {isEnglish ? 'On Your CV' : 'Sur Votre CV'}
                        </h4>
                        <p className="text-gray-600 text-xs">
                          {isEnglish 
                            ? 'Add your professional bio to your CV summary section'
                            : 'Ajoutez votre bio professionnelle à la section résumé de votre CV'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                        <Linkedin className="w-4 h-4 text-[#0077B5]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 text-sm mb-1">
                          {isEnglish ? 'On LinkedIn' : 'Sur LinkedIn'}
                        </h4>
                        <p className="text-gray-600 text-xs">
                          {isEnglish 
                            ? 'Share your persona badge and key strengths on your profile'
                            : 'Partagez votre badge persona et vos points forts sur votre profil'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                        <Users className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 text-sm mb-1">
                          {isEnglish ? 'In Interviews' : 'En Entretien'}
                        </h4>
                        <p className="text-gray-600 text-xs">
                          {isEnglish 
                            ? 'Use the interview tips to prepare compelling examples'
                            : 'Utilisez les conseils d\'entretien pour préparer des exemples convaincants'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                        <TrendingUp className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 text-sm mb-1">
                          {isEnglish ? 'Career Planning' : 'Planification de Carrière'}
                        </h4>
                        <p className="text-gray-600 text-xs">
                          {isEnglish 
                            ? 'Use your strengths to identify ideal career paths'
                            : 'Utilisez vos forces pour identifier les parcours de carrière idéaux'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

            </div>

            {/* Document Footer */}
            <div className="px-6 sm:px-8 py-4 bg-gray-50 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <img src="/edomatch-logo.png" alt="EdoMatch" className="h-5 opacity-60" />
                  <span>•</span>
                  <span>{isEnglish ? 'Professional Personality Assessment' : 'Évaluation de Personnalité Professionnelle'}</span>
                </div>
                <span>{new Date().toLocaleDateString(isEnglish ? 'en-US' : 'fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* CTA Section - Dynamic based on CV status */}
        <div className="mt-6 space-y-4">
          {hasCVProfile ? (
            <div className="bg-white border border-green-200 rounded-2xl p-6 shadow-sm">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                {/* CV Completion Progress Circle */}
                <div className="relative w-16 h-16 flex-shrink-0">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                    <circle
                      className="text-gray-200"
                      stroke="currentColor"
                      strokeWidth="3"
                      fill="none"
                      cx="18"
                      cy="18"
                      r="15"
                    />
                    <circle
                      className="text-[#5BA946]"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      fill="none"
                      cx="18"
                      cy="18"
                      r="15"
                      strokeDasharray={`${cvCompletionPercent * 0.94}, 100`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold text-[#5BA946]">{cvCompletionPercent}%</span>
                  </div>
                </div>
                
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {cvCompletionPercent >= 80 
                      ? (isEnglish ? 'Your CV is ready!' : 'Votre CV est prêt !')
                      : (isEnglish ? 'Your CV is in progress' : 'Votre CV est en cours')}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {cvCompletionPercent >= 80 
                      ? (isEnglish 
                          ? 'Great job! View your complete professional profile.'
                          : 'Excellent ! Consultez votre profil professionnel complet.')
                      : (isEnglish 
                          ? 'Continue building your CV to create a complete professional document.'
                          : 'Continuez à compléter votre CV pour créer un document professionnel complet.')}
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  {cvCompletionPercent < 80 && (
                    <Button onClick={() => navigate('/chat')} variant="outline" className="border-[#5BA946] text-[#5BA946]">
                      <PenLine className="w-4 h-4 mr-2" />
                      {isEnglish ? 'Continue CV' : 'Continuer le CV'}
                    </Button>
                  )}
                  <Button onClick={() => navigate('/complete-profile')} className="bg-[#5BA946] hover:bg-[#4a9339] text-white">
                    <Eye className="w-4 h-4 mr-2" />
                    {isEnglish ? 'Complete Profile' : 'Profil complet'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="w-14 h-14 bg-[#5BA946] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {isEnglish ? 'Complete your professional profile' : 'Complétez votre profil professionnel'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {isEnglish 
                      ? 'Create a CV to have a complete document showcasing your experience and personality.'
                      : 'Créez un CV pour avoir un document complet présentant votre expérience et votre personnalité.'}
                  </p>
                </div>
                <Button onClick={() => navigate('/')} className="bg-[#5BA946] hover:bg-[#4a9339] text-white">
                  <PenLine className="w-4 h-4 mr-2" />
                  {isEnglish ? 'Create my CV' : 'Créer mon CV'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="mt-6 text-center">
          <p className="text-gray-500 text-xs">
            {isEnglish 
              ? `Retrieval code: ${retrievalCode} • Save this code to access your results in the EdoMatch app`
              : `Code de récupération : ${retrievalCode} • Conservez ce code pour accéder à vos résultats dans l'app EdoMatch`}
          </p>
        </div>
      </main>

      {/* Download Gate Modal */}
      <DownloadGateModal
        isOpen={showDownloadGate}
        onClose={closeDownloadGate}
        onSuccess={onLeadCaptured}
        profileId={profile?.id}
        firstName={profile?.first_name}
        downloadType={downloadType}
        language={isEnglish ? 'en' : 'fr'}
      />
    </div>
  );
}
